import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';

import { type BrandConfig, type BaseColors, brandApi } from '@bv/api/brand';
import { useCookies, useLocalStorage } from '@bv/composables';
import { generateColorScale } from '@bv/utils';

/**
 * Brand store
 */
export const useBrandStore = defineStore('bvBrand', () => {
  /**
   * Indicates whether a brand configuration fetch is currently in progress
   */
  const isConfigFetching = computed<boolean>(() => !!fetchPromise.value);

  /**
   * Stores the active fetch promise to prevent duplicate requests
   * @private
   */
  const fetchPromise = ref<Promise<BrandConfig> | null>(null);

  /**
   * Current brand configuration
   */
  const config = ref<BrandConfig | null>(null);

  /**
   * Initializes the brand store by loading config from cookies and fetching latest data
   */
  const initBrand = () => {
    // TODO Remove (cookie remover) after 20-12-2025
    const cookie = useCookies();
    cookie.remove('brand');

    const { get } = useLocalStorage();

    config.value = get<BrandConfig>('brand');

    fetchConfig();
  };

  /**
   * Updates CSS custom properties for brand colors based on current configuration
   * Removes custom colors if using default brand or generates color scale from primary color
   * @private
   */
  const updateColor = () => {
    const colorLevels = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900];

    if (!config.value || !config.value.colors || config.value.isDefault) {
      colorLevels.forEach((level) => {
        document.documentElement.style.removeProperty(`--base-${level}`);
      });

      return;
    }

    const colors = config.value.colors;

    if ('primary' in colors) {
      const generatedColors = generateColorScale(colors.primary);

      Object.entries(generatedColors).forEach(([name, color]) => {
        document.documentElement.style.setProperty(`--base-${name}`, color);
      });
    } else {
      const generatedColors = generateColorScale(colors['primary-500']);
      colorLevels.forEach((level) => {
        const colorName = `base-${level}` as keyof BaseColors;
        if (colors[colorName]) {
          document.documentElement.style.setProperty(`--base-${level}`, colors[colorName] ?? null);
        } else if (colors['primary-500']) {
          const levelName = level as keyof typeof generatedColors;
          const generatedColor = generatedColors[levelName];
          document.documentElement.style.setProperty(`--base-${level}`, generatedColor);
        }
      });
    }
  };

  /**
   * Updates the page favicon based on current brand configuration
   * Uses brand logo if not default, otherwise uses default favicon
   * @private
   */
  const updateFavicon = (): void => {
    const newUrl = config.value?.isDefault === false ? config.value.assets.logo : '/favicon.svg';

    let iconLink = document.querySelector("link[rel='icon']") as HTMLLinkElement | undefined;

    if (iconLink) {
      iconLink.href = newUrl;
    } else {
      iconLink = document.createElement('link');
      iconLink.rel = 'icon';
      iconLink.href = newUrl;

      document.head.appendChild(iconLink);
    }
  };

  /**
   * Watch for config changes and update favicon and colors accordingly
   */
  watch(
    () => config.value,
    () => {
      updateFavicon();
      updateColor();
    },
    { deep: true },
  );

  /**
   * Fetches brand configuration from API
   * Prevents duplicate requests by reusing existing fetch promise
   * Caches result in localStorage
   *
   * @returns {Promise<BrandConfig>} resolving to brand configuration
   */
  const fetchConfig = async (): Promise<BrandConfig> => {
    // Return existing promise if fetch is already in progress
    if (fetchPromise.value) {
      return fetchPromise.value;
    }

    fetchPromise.value = brandApi
      .config()
      .then((response) => {
        const { set } = useLocalStorage();
        set('brand', response);

        config.value = response;
        return response;
      })
      .finally(() => {
        fetchPromise.value = null;
      });
    return fetchPromise.value;
  };

  return {
    // Indicates if brand config is currently being fetched
    isConfigFetching,
    // Current brand configuration
    config,

    // Fetches brand configuration from API
    fetchConfig,
    // Initializes brand store with cached config and fetches latest
    initBrand,
  };
});
